/**
 * Message bubble component for the chat interface.
 * Modern gradient bubbles with glass AI messages.
 */

import { Box, Typography } from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import BuildIcon from '@mui/icons-material/Build';
import type { AgentMessageDto } from '../../types/session';
import { MessageRole } from '../../types/session';
import { AudioMessage } from './AudioMessage';
import { DocumentMessage } from './DocumentMessage';
import { ImageMessage } from './ImageMessage';
import { VideoMessage } from './VideoMessage';

interface MessageBubbleProps {
  message: AgentMessageDto;
}

/**
 * Modern message bubble with gradient for user and glass for AI.
 */
export const MessageBubble = ({ message }: MessageBubbleProps) => {
  const { role, content, createdAt, tokensUsed, cost, fileId, mediaType, processedContentJson } = message;
  const isUser = role === MessageRole.User;
  const isSystem = role === MessageRole.System;
  const isAssistant = role === MessageRole.Assistant;

  const formatTimestamp = (ts?: string) => {
    if (!ts) return '';
    try {
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return ts;
    }
  };

  const renderAttachment = () => {
    if (!fileId) return null;
    const mt = (mediaType || '').toLowerCase();
    if (mt === 'image') return <ImageMessage fileId={fileId} processedContentJson={processedContentJson} />;
    if (mt === 'audio') return <AudioMessage fileId={fileId} processedContentJson={processedContentJson} />;
    if (mt === 'video') return <VideoMessage fileId={fileId} processedContentJson={processedContentJson} />;
    return <DocumentMessage fileId={fileId} processedContentJson={processedContentJson} />;
  };

  const getRoleIcon = () => {
    if (isUser) return <PersonIcon sx={{ fontSize: '0.875rem' }} />;
    if (isAssistant) return <SmartToyIcon sx={{ fontSize: '0.875rem' }} />;
    if (isSystem) return <SettingsIcon sx={{ fontSize: '0.875rem' }} />;
    return <BuildIcon sx={{ fontSize: '0.875rem' }} />;
  };

  const getRoleLabel = () => {
    if (isUser) return 'You';
    if (isAssistant) return 'AI Assistant';
    if (isSystem) return 'System';
    return 'Tool';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 3,
        animation: 'fade-in 200ms ease-out',
      }}
    >
      <Box
        sx={{
          maxWidth: '75%',
          minWidth: 200,
        }}
      >
        {/* Role Label */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            justifyContent: isUser ? 'flex-end' : 'flex-start',
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: 'var(--mui-shape-customBorderRadius-xs)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...(isUser
                ? { background: 'var(--gradient-primary)', color: '#fff' }
                : isAssistant
                  ? { background: 'var(--gradient-primary-subtle)', color: 'var(--mui-palette-primary-main)' }
                  : { background: 'rgba(148, 163, 184, 0.1)', color: 'var(--mui-palette-text-secondary)' }),
            }}
          >
            {getRoleIcon()}
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'var(--mui-palette-text-secondary)' }}>
            {getRoleLabel()}
          </Typography>
          {createdAt && (
            <Typography variant="caption" sx={{ color: 'var(--mui-palette-text-disabled)' }}>
              {formatTimestamp(createdAt)}
            </Typography>
          )}
        </Box>

        {/* Message Bubble */}
        <Box
          sx={{
            p: 3,
            borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
            ...(isUser
              ? {
                  background: 'var(--gradient-primary)',
                  color: '#fff',
                  borderBottomRightRadius: 4,
                }
              : {
                  background: 'var(--glass-bg)',
                  backdropFilter: 'blur(12px)',
                  WebkitBackdropFilter: 'blur(12px)',
                  border: '1px solid var(--glass-border)',
                  borderBottomLeftRadius: 4,
                }),
          }}
        >
          {(content || isUser) && (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                color: isUser ? '#fff' : 'var(--mui-palette-text-primary)',
                '& code': {
                  backgroundColor: isUser ? 'rgba(255, 255, 255, 0.15)' : 'rgba(139, 92, 246, 0.1)',
                  padding: '2px 6px',
                  borderRadius: 'var(--mui-shape-customBorderRadius-xs)',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: '0.8em',
                },
                '& pre': {
                  backgroundColor: isUser ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.2)',
                  padding: '12px',
                  borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                  overflow: 'auto',
                  fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                  fontSize: '0.8em',
                  border: '1px solid var(--glass-border)',
                },
              }}
            >
              {content?.trim() ? content : isUser ? '(empty message)' : content}
            </Typography>
          )}

          {renderAttachment()}
        </Box>

        {/* Metadata */}
        {(tokensUsed || cost) && (
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              gap: 2,
              justifyContent: isUser ? 'flex-end' : 'flex-start',
            }}
          >
            {tokensUsed != null && tokensUsed > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--mui-palette-text-disabled)', fontSize: '0.6875rem' }}>
                {tokensUsed.toLocaleString()} tokens
              </Typography>
            )}
            {cost != null && cost > 0 && (
              <Typography variant="caption" sx={{ color: 'var(--mui-palette-text-disabled)', fontSize: '0.6875rem' }}>
                ${cost.toFixed(4)}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
